/**
 * Browser Notification API helpers for in-app inbox events while the tab is open.
 * Does not use Web Push / service workers.
 */

export type DesktopPermission = NotificationPermission | 'unsupported';

export type DesktopNotificationUser = {
    notificationPreferences?: {
        channels?: string[];
    } | null;
} | null | undefined;

export type DesktopNotificationPayload = {
    _id?: string;
    title?: string;
    body?: string | null;
    link?: string | null;
};

const DEFAULT_ICON = '/android-chrome-192x192.png';

export function isDesktopNotificationSupported(): boolean {
    return typeof window !== 'undefined' && typeof Notification !== 'undefined';
}

export function getDesktopNotificationPermission(): DesktopPermission {
    if (!isDesktopNotificationSupported())
        return 'unsupported';
    return Notification.permission;
}

export async function requestDesktopNotificationPermission(): Promise<DesktopPermission> {
    if (!isDesktopNotificationSupported())
        return 'unsupported';
    return Notification.requestPermission();
}

export function userWantsDesktopNotifications(user: DesktopNotificationUser): boolean {
    const channels = user?.notificationPreferences?.channels;
    return Array.isArray(channels) && channels.includes('desktop');
}

export function shouldShowDesktopNotification(user: DesktopNotificationUser): boolean {
    return isDesktopNotificationSupported()
        && Notification.permission === 'granted'
        && userWantsDesktopNotifications(user)
        && typeof document !== 'undefined'
        && document.hidden;
}

export function resolveNotificationPath(link: string): string | null {
    try {
        const url = new URL(link, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return link.startsWith('/') ? link : null;
    }
}

export function showDesktopNotification(
    notification: DesktopNotificationPayload,
    options?: { icon?: string }
): Notification | null {
    if (!isDesktopNotificationSupported() || Notification.permission !== 'granted')
        return null;

    const desktopNotification = new Notification(notification.title || 'Yoda', {
        body: notification.body || undefined,
        icon: options?.icon || DEFAULT_ICON,
        tag: notification._id || undefined,
    });

    desktopNotification.onclick = () => {
        window.focus();
        desktopNotification.close();
        if (!notification.link)
            return;
        const path = resolveNotificationPath(notification.link);
        if (path)
            window.location.assign(path);
    };

    return desktopNotification;
}

export function maybeShowDesktopNotification(
    user: DesktopNotificationUser,
    notification: DesktopNotificationPayload
): void {
    if (!shouldShowDesktopNotification(user))
        return;
    showDesktopNotification(notification);
}
